// client/src/components/wholesaler/OrderDiscountModal.js
import React, { useState, useEffect } from 'react';
import { X, Percent, Tag } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API_URL, getAuthHeaders } from '../../services/api';

const OrderDiscountModal = ({ order, onClose, onDiscountApplied }) => {
  const { t } = useTranslation('wholesaler');
  const [percentage, setPercentage] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Check if order already has a discount
  const hasExistingDiscount = order?.discount?.type !== 'none' && order?.discount?.percentage > 0;
  
  useEffect(() => {
    if (hasExistingDiscount) {
      setPercentage(order.discount.percentage.toString());
      setReason(order.discount.reason || '');
    }
  }, [hasExistingDiscount, order]);
  
  // Calculate preview (Net Prices for B2B)
  const subtotal = order?.totals?.subtotal || 0;
  const discountAmount = percentage ? (subtotal * parseFloat(percentage)) / 100 : 0;
  const afterDiscount = Math.max(0, subtotal - discountAmount);
  
  const deliveryFee = order?.totals?.deliveryFee || 0;
  const total = afterDiscount + deliveryFee;

  const handleApply = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // ✅ Capture the response
      const response = await axios.post(`${API_URL}/wholesalers/orders/${order._id}/discount`, {
        percentage: parseFloat(percentage),
        reason
      }, { headers: getAuthHeaders() });
      
      // ✅ Pass the updated order back to the parent!
      onDiscountApplied(response.data.order);
      onClose();
    } catch (err) {
      console.error('Error applying discount:', err);
      const msg = err.response?.data?.message || 'Failed to apply discount';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // ✅ Capture the response
      const response = await axios.delete(`${API_URL}/wholesalers/orders/${order._id}/discount`, { 
        headers: getAuthHeaders() 
      });
      
      // ✅ Pass the updated order back to the parent!
      onDiscountApplied(response.data.order);
      onClose();
    } catch (err) {
      console.error('Error removing discount:', err);
      setError(err.response?.data?.message || 'Failed to remove discount');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all scale-100">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-pink-600 to-rose-600 p-6 flex justify-between items-center text-white shadow-md">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
              <Percent className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold">{hasExistingDiscount ? t('orders.editDiscount') : t('orders.applyDiscount')}</h2>
              <p className="text-pink-100 text-sm">{t('orders.specialOffer')}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Body */}
        <div className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
              <Tag className="w-4 h-4 text-pink-500" />
              {t('orders.discountPercentage')}
            </label>
            <div className="relative group">
              <input
                type="number"
                min="0"
                max="100"
                value={percentage}
                onChange={(e) => setPercentage(e.target.value)}
                className="w-full pl-4 pr-12 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-pink-100 focus:border-pink-500 transition-all font-bold text-lg text-gray-800"
                placeholder="0"
              />
              <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 font-bold text-xl">%</span>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              {t('orders.discountReason')}
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-pink-100 focus:border-pink-500 transition-all resize-none text-sm"
              rows="2"
              placeholder={t('orders.discountReasonPlaceholder')}
            />
          </div>
          
          {/* Summary Preview Card */}
          <div className="bg-gradient-to-br from-gray-50 to-pink-50 rounded-xl p-5 space-y-3 border border-pink-100 shadow-inner">
            <div className="flex justify-between text-gray-600 text-sm">
              <span>{t('orders.subtotal')}</span>
              <span>€{subtotal.toFixed(2)}</span>
            </div>
            
            {percentage > 0 && (
              <div className="flex justify-between text-pink-600 font-bold items-center py-1 bg-pink-100/50 px-2 rounded-lg -mx-2">
                <span className="flex items-center gap-1"><Percent className="w-3 h-3" /> {t('orders.discount')} ({percentage}%)</span>
                <span>-€{discountAmount.toFixed(2)}</span>
              </div>
            )}
            
            <div className="border-t border-gray-200 my-2"></div>

            <div className="flex justify-between text-gray-600 text-sm">
              <span>{t('orders.newSubtotal')}</span>
              <span>€{afterDiscount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-gray-600 text-sm">
              <span>{t('orders.deliveryFee')}</span>
              <span>€{deliveryFee.toFixed(2)}</span>
            </div>
            
            <div className="border-t-2 border-dashed border-gray-300 pt-3 mt-2 flex justify-between items-end">
              <div>
                <span className="text-gray-600 font-medium">{t('orders.totalNet')}</span>
              </div>
              <span className="text-2xl font-bold text-gray-900">€{total.toFixed(2)}</span>
            </div>
            <p className="text-xs text-gray-500 italic text-center mt-2">{t('orders.vatDisclaimer')}</p>
          </div>
          
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3 animate-pulse">
              <div className="w-2 h-2 bg-red-500 rounded-full"></div>
              <p className="text-sm text-red-700 font-medium">{error}</p>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="flex gap-3 p-6 border-t border-gray-100 bg-gray-50">
          {hasExistingDiscount && (
            <button
              onClick={handleRemove}
              disabled={loading}
              className="px-4 py-3 text-red-600 hover:bg-red-50 rounded-xl transition-colors font-semibold disabled:opacity-50 text-sm"
            >
              {loading ? t('common.processing') : t('orders.removeDiscount')}
            </button>
          )}
          
          <div className="flex-1 flex gap-3 justify-end">
            <button
              onClick={onClose}
              disabled={loading}
              className="px-6 py-3 border-2 border-gray-200 text-gray-600 rounded-xl hover:bg-gray-100 hover:border-gray-300 transition-all font-semibold"
            >
              {t('common.cancel')}
            </button>
            
            <button
              onClick={handleApply}
              disabled={loading || !percentage || !reason.trim()}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-pink-600 to-rose-600 text-white rounded-xl hover:from-pink-700 hover:to-rose-700 shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed font-bold transform active:scale-95"
            >
              {loading ? t('common.processing') : t('orders.apply')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDiscountModal;